import { defineStore } from 'pinia';
import { ref } from 'vue';
import api from '../services/api';

export interface ActiveTimer {
  id: number;
  taskId: number;
  taskTitle: string;
  startTime: string;
  isRunning: boolean;
}

/**
 * The signed-in user's running task timer (topbar). Per user: reset on every
 * sign-in / sign-out (stores/sessionScope.ts) and when tasks.view is lost
 * (DashboardLayout); an answer that started before a reset is dropped.
 */
export const useTimerStore = defineStore('timer', () => {
  const activeTimer = ref<ActiveTimer | null>(null);
  const elapsed = ref(0);
  const interval = ref<ReturnType<typeof setInterval> | null>(null);
  /** Bumped by reset(): a response from before belongs to someone else. */
  let generation = 0;

  async function fetchActiveTimer() {
    const gen = generation;
    try {
      const response = await api.get('/tasks/timer/active');
      if (gen !== generation) return;
      activeTimer.value = response.data.data;
      if (activeTimer.value) {
        startInterval();
      } else {
        stopInterval();
      }
    } catch {
      // Silently fail - timer is not critical
    }
  }

  function startInterval() {
    if (interval.value) clearInterval(interval.value);
    if (activeTimer.value) {
      const startTime = new Date(activeTimer.value.startTime).getTime();
      elapsed.value = Math.floor((Date.now() - startTime) / 1000);
      interval.value = setInterval(() => {
        elapsed.value = Math.floor((Date.now() - startTime) / 1000);
      }, 1000);
    }
  }

  function stopInterval() {
    if (interval.value) {
      clearInterval(interval.value);
      interval.value = null;
    }
    elapsed.value = 0;
  }

  async function startTimer(taskId: number) {
    await api.post(`/tasks/${taskId}/timer/start`);
    await fetchActiveTimer();
  }

  async function stopTimer() {
    if (!activeTimer.value) return;
    await api.post(`/tasks/${activeTimer.value.taskId}/timer/stop`);
    stopInterval();
    activeTimer.value = null;
  }

  function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }

  function formatDurationShort(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  }

  function cleanup() {
    stopInterval();
  }

  /** Forget the timer (another user, sign-out, tasks.view lost). */
  function reset() {
    generation++;
    stopInterval();
    activeTimer.value = null;
  }

  return {
    activeTimer,
    elapsed,
    fetchActiveTimer,
    startTimer,
    stopTimer,
    formatDuration,
    formatDurationShort,
    cleanup,
    reset,
  };
});
